import './../vignette.style.scss';
import './lineGraph.style.scss';

import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Line } from '@nivo/line';

import { handleResize } from '../vignette.utils';

import CustomTooltip from './CustomTooltip';

interface PointGraph {
  x: number;
  y: number;
}

interface GraphData {
  data: PointGraph[];
  id: string;
}

interface LineGraphProps {
  data: GraphData[];
  nbElmentToDisplay: number;
  unit?: string;
  indice: string;
  pdfTitle?: string;
}

export const LineGraph = ({
  data,
  nbElmentToDisplay,
  unit,
  indice = 'Ic',
  pdfTitle,
  ...rest
}: LineGraphProps) => {
  const parentRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const colors = [
    '#003c51',
    '#31baae',
    '#a7eade',
    '#038c8c',
    '#015364',
    '#026e78',
    '#159880',
    '#476d66',
    '#4f8b81',
    '#5ab9a8',
    '#8ec2b9',
    '#0ad1ae',
    '#5bdcc6',
  ];

  const { t } = useTranslation();

  useEffect(() => {
    handleResize(parentRef, setDimensions);

    window.addEventListener('resize', () => handleResize(parentRef, setDimensions));
    return () =>
      window.removeEventListener('resize', () =>
        handleResize(parentRef, setDimensions)
      );
  }, [parentRef.current]);

  function formatDateXAxis(nbElement = 11) {
    if (!data || !data[0]?.data || data[0].data.length === 0) {
      return [];
    }

    const firstTick = data[0].data[0].x;
    const lastTick = data[0].data[data[0].data.length - 1].x;

    if (firstTick === undefined || lastTick === undefined) {
      return [];
    }

    const tickInterval = (lastTick - firstTick) / nbElement;
    const middleTicks = Array.from({ length: nbElement - 1 }, (_, i) =>
      Math.round(firstTick + tickInterval * (i + 1))
    );

    return [firstTick, ...middleTicks, lastTick];
  }

  return (
    <>
      <h2>{t(pdfTitle)}</h2>
      <div ref={parentRef} style={{ height: 400 }} {...rest}>
        <div className="line-graph__acv-wrapper--items">
          {data.map((key, index) => (
            <div className="line-graph__acv-item">
              <div
                style={{
                  background: colors[index],
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  marginRight: '5px',
                }}
              />
              {key.id.length > 18 ? `${key.id.substring(0, 18)}...` : key.id}
            </div>
          ))}
        </div>
        <Line
          data={data}
          width={dimensions.width}
          height={dimensions.height}
          enableGridX={false}
          enablePoints={false}
          lineWidth={4}
          margin={{ top: 20, right: 50, bottom: 50, left: 60 }}
          axisBottom={{
            tickValues: formatDateXAxis(nbElmentToDisplay),
            legend: t('APP.LINE_GRAPHIC.YEARS', {
              defaultValue: 'Années',
            }),
            legendOffset: 40,
          }}
          axisLeft={{
            legend: `${indice} ${
              unit === 'm²' ? ' (kg éq. CO₂/m²)' : ' (kg éq. CO₂)'
            }`,
            legendOffset: -55,
          }}
          colors={colors}
          useMesh={true}
          enableSlices="x"
          sliceTooltip={({ slice }) => <CustomTooltip slice={slice} />}
        />
      </div>
    </>
  );
};
